import {
    buildInitialFilters,
    composeColumnRenderer,
    FormInputCell,
    GridColumnType,
    renderColumnFilter
} from "@repo/design-system/components/grids";
import { DropdownActions, Iconify, NoRecordFound } from "@repo/design-system/components/ui";
import { FilterMatchMode } from "primereact/api";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
// import { useDebounce } from "../../hooks/useDebounce";
import debounce from "lodash/debounce";
import "./GridBuilder.scss";
import { DefaultActions } from "./config/ActionConfig";

const GridBuilderComponent = ({
    gridWrapperClass,
    dataSource = [],
    config = [],
    paginator = true,
    rows = 10,
    rowsPerPageOptions = [5, 10, 25, 50, 100, 200],
    sortMode = "multiple",
    tableStyle = { minWidth: "50rem" },
    actionHandlers = {},
    columnEvents = {},
    scrollHeight,
    onSearch,
    onSort,
    onPage,
    noRecordFoundMessage = "No Record Found",
    extraFields,
    ...rest
}) => {

    const { columns, filtersConfig, showAllOption } = config;

    // PrimeReact's Paginator computes page/totalPages as Math.floor/ceil(x / rows) internally,
    // so a literal rows=0 for "Show All" produces NaN/Infinity and breaks the paginator silently.
    // Instead, size the "Show All" page to the actual record count — a real, self-describing
    // value rather than an arbitrary constant — and translate it back to rows: 0 on the way out.
    // Kept strictly above the largest standard option so it never collides with one of them.
    const largestStandardOption = Math.max(...rowsPerPageOptions, 0);
    const showAllRowsValue = Math.max(rest.totalRecords ?? dataSource.length, largestStandardOption + 1);

    const isShowingAll = showAllOption && rows === 0;
    const effectiveRows = isShowingAll ? showAllRowsValue : rows;

    const effectiveRowsPerPageOptions = useMemo(() => {
        if (!showAllOption) return rowsPerPageOptions;
        return [...rowsPerPageOptions, showAllRowsValue];
    }, [showAllOption, rowsPerPageOptions, showAllRowsValue]);

    const showAllPaginatorTemplate = useMemo(() => {
        if (!showAllOption) return undefined;
        return {
            layout: "CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown",
            RowsPerPageDropdown: (options) => (
                <Dropdown
                    value={options.value}
                    options={effectiveRowsPerPageOptions.map((n) => ({
                        label: n === showAllRowsValue ? "Show All" : String(n),
                        value: n,
                    }))}
                    onChange={options.onChange}
                    appendTo={options.appendTo}
                />
            ),
        };
    }, [showAllOption, effectiveRowsPerPageOptions, showAllRowsValue]);

    const handleInternalPage = useCallback((e) => {
        if (showAllOption && e.rows === showAllRowsValue) {
            onPage?.({ ...e, rows: 0, first: 0, page: 0 });
            return;
        }
        onPage?.(e);
    }, [onPage, showAllOption, showAllRowsValue]);

    const [multiSortMeta, setMultiSortMeta] = useState([]);

    const [filters, setFilters] = useState({
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
        ...buildInitialFilters(columns)
    });

    const [searchText, setSearchText] = useState("");

    // Dynamic row expansion state management
    const [internalExpandedRows, setInternalExpandedRows] = useState(null);
    const expandedRows = rest.expandedRows !== undefined ? rest.expandedRows : internalExpandedRows;

    const isDefaultExpandedAll = Boolean(
        rest.defaultExpandedAll ?? config?.defaultExpandedAll ?? rest.expandAllByDefault
    );
    const isDefaultExpandFirst = Boolean(
        rest.defaultExpandFirst ?? config?.defaultExpandFirst ?? rest.expandFirstRowByDefault
    );

    useEffect(() => {
        if (rest.rowExpansionTemplate && Array.isArray(dataSource) && dataSource.length > 0) {
            const dataKeyField = rest.dataKey || "id";
            if (isDefaultExpandedAll) {
                const initialMap = {};
                dataSource.forEach((item, idx) => {
                    const key = item[dataKeyField] ?? idx;
                    if (key != null) {
                        initialMap[key] = true;
                    }
                });
                setInternalExpandedRows(initialMap);
            } else if (isDefaultExpandFirst) {
                const firstItem = dataSource[0];
                const firstKey = firstItem?.[dataKeyField] ?? 0;
                if (firstKey != null) {
                    setInternalExpandedRows({ [firstKey]: true });
                } else {
                    setInternalExpandedRows(null);
                }
            } else {
                setInternalExpandedRows(null);
            }
        } else {
            setInternalExpandedRows(null);
        }
    }, [dataSource, isDefaultExpandedAll, isDefaultExpandFirst, rest.dataKey]);

    const handleInternalRowToggle = useCallback((e) => {
        setInternalExpandedRows(e.data);
        if (rest.onRowToggle) {
            rest.onRowToggle(e);
        }
    }, [rest.onRowToggle]);

    const handleInternalRowClick = useCallback((e) => {
        if (rest.onRowClick) {
            rest.onRowClick(e);
        } else if (rest.rowExpansionTemplate && rest.expandOnRowClick !== false) {
            const dataKeyField = rest.dataKey || "id";
            const rowId = e?.data?.[dataKeyField];
            if (rowId != null) {
                setInternalExpandedRows((prev) => {
                    const current = prev ? { ...prev } : {};
                    if (current[rowId]) {
                        delete current[rowId];
                    } else {
                        current[rowId] = true;
                    }
                    return current;
                });
            }
        }
    }, [rest.onRowClick, rest.rowExpansionTemplate, rest.expandOnRowClick, rest.dataKey]);

    const onSearchRef = useRef(onSearch);
    useEffect(() => {
        onSearchRef.current = onSearch;
    }, [onSearch]);

    const debouncedSearch = useMemo(() =>
        debounce((value) => {
            onSearchRef.current?.(value);
            setFilters(prev => ({ ...prev, global: { ...prev.global, value: value || null } }));
        }, filtersConfig?.debounceTime || 400),
        [filtersConfig?.debounceTime]
    );

    // Cancel debounce on unmount
    useEffect(() => {
        return () => {
            debouncedSearch.cancel();
        };
    }, [debouncedSearch]);

    const triggerSearch = useCallback((value) => {
        debouncedSearch.cancel();
        onSearchRef.current?.(value);
        setFilters(prev => ({ ...prev, global: { ...prev.global, value: value || null } }));
    }, [debouncedSearch]);

    const handleInternalSort = useCallback((e) => {
        const newMultiSortMeta = e.multiSortMeta || [];
        setMultiSortMeta(newMultiSortMeta);
        if (!onSort) return;
        const order = newMultiSortMeta[0]?.order ?? e.sortOrder;
        const sortDir = order === 1 ? 'desc' : 'asc';
        onSort({ sortDir, sortField: newMultiSortMeta[0]?.field ?? e.sortField });
    }, [onSort]);

    const handleSearchChange = (value) => {
        setSearchText(value);
        debouncedSearch(value);
    };

    const handleSearchClick = (value) => {
        const val = typeof value === 'string' ? value : searchText;
        triggerSearch(val);
    };

    const handleClearClick = () => {
        setSearchText("");
        triggerSearch("");
    };

    const renderActions = useCallback((actions) => (rowData) => {
        if (!actions || actions.length === 0) return null;

        return (
            <div className="grid-actions">
                {actions.map((action, index) => {
                    const handlerFn = actionHandlers[action.handler];
                    return (
                        <Iconify
                            key={index}
                            icon={action.icon}
                            width={action.iconWidth}
                            ttLabel={action?.ttLabel ?? ""}
                            targetId={action?.targetId ?? ""}
                            className={`action-icon ${action.type}`}
                            onClick={handlerFn ? () => handlerFn(rowData) : undefined}
                        />
                    );
                })}
            </div>
        );
    }, [actionHandlers]);

    const resolvedColumns = useMemo(() => {
        return columns?.map((col) => {
            if (col.type !== GridColumnType.ACTION) return col;

            if (col.useDropdown) {
                const hasDropdownActions = Array.isArray(dataSource) && dataSource.length > 0
                    ? dataSource.some(row => {
                        const custom = typeof col.getCustomActions === "function" ? col.getCustomActions(row) : (col.customActions || []);
                        const defaults = Object.values(col.defaultActions || {}).some(Boolean);
                        return defaults || custom.length > 0;
                    })
                    : (Object.values(col.defaultActions || {}).some(Boolean) || (col.customActions || []).length > 0);

                if (!hasDropdownActions) return { ...col, _resolvedActions: null };

                const dropdownRenderer = (rowData) => (
                    <DropdownActions
                        rowData={rowData}
                        config={{
                            defaultActions: col.defaultActions || {},
                            customActions: typeof col.getCustomActions === "function"
                                ? col.getCustomActions(rowData)
                                : (col.customActions || []),
                        }}
                        actionHandlers={actionHandlers}
                    />
                );
                return {
                    ...col,
                    _resolvedActions: true,
                    _actionRenderer: dropdownRenderer,
                };
            }

            if (typeof col.getActions === "function") {
                // menuActionKeys moves those actions (default or custom) from the inline icons into the kebab menu
                const menuKeys = col.menuActionKeys || [];
                const toMenuAction = (action) => ({
                    ...action,
                    actionType: action.handler ?? action.key,
                    label: action.label ?? action.ttLabel,
                    ttLabel: undefined,
                    iconWidth: 14,
                    className: action.menuClassName ?? (action.key === "DELETE" ? "danger" : undefined),
                });
                const getInlineActions = (row) => (col.getActions(row, col) || [])
                    .filter((action) => !menuKeys.includes(action.key));
                // Optional kebab menu rendered after the inline icons (e.g. Edit / Delete / Publish)
                const getMenuActions = (row) => [
                    ...((typeof col.getMenuActions === "function" ? col.getMenuActions(row, col) : null) || []),
                    ...(menuKeys.length
                        ? (col.getActions(row, col) || []).filter((action) => menuKeys.includes(action.key)).map(toMenuAction)
                        : []),
                ];
                const rowHasActions = (row) => getInlineActions(row).length > 0
                    || getMenuActions(row).length > 0;
                const hasActions = Array.isArray(dataSource) && dataSource.length > 0
                    ? dataSource.some(rowHasActions)
                    : rowHasActions({});

                if (!hasActions) return { ...col, _resolvedActions: null };

                return {
                    ...col,
                    _resolvedActions: true,
                    _actionRenderer: (rowData) => {
                        const inlineActions = renderActions(getInlineActions(rowData))(rowData);
                        const menuActions = getMenuActions(rowData);
                        if (!menuActions.length) return inlineActions;
                        return (
                            <div className="grid-actions grid-actions-with-menu">
                                {inlineActions}
                                <DropdownActions
                                    rowData={rowData}
                                    config={{ customActions: menuActions }}
                                    actionHandlers={actionHandlers}
                                />
                            </div>
                        );
                    },
                };
            }

            let actions = [];

            if (col.defaultActions) {
                actions = DefaultActions.filter(
                    a => col.defaultActions[a.key]
                );
            }

            if (Array.isArray(col.customActions)) {
                actions = [...actions, ...col.customActions];
            }

            if (!actions.length) return { ...col, _resolvedActions: null };

            return {
                ...col,
                _resolvedActions: actions,
                _actionRenderer: renderActions(actions)
            };
        });
    }, [columns, renderActions, actionHandlers, dataSource]);

    const getColumnBody = useCallback(
        (col) => composeColumnRenderer(col, columnEvents),
        [columnEvents]
    );

    const globalFilterFields = useMemo(() => {
        return columns?.filter(col => col.field && col.type !== GridColumnType.ACTION)
            .map(col => col.field);
    }, [columns]);

    return (
        <div className={`grid-wrapper ${gridWrapperClass || ""}`}>
            {(filtersConfig?.enabled || extraFields) && (
                <div className="grid-search-bar mb-3"
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            handleSearchClick();
                        }
                    }}>
                    {filtersConfig?.enabled && (
                        <FormInputCell id="search" name="search" cssClass=""
                            value={searchText} onBlur={() => ""}
                            onChange={(dataField, value) => handleSearchChange(value)}
                            handleClearClick={handleClearClick}
                            handleSearchClick={handleSearchClick}
                            {...filtersConfig}
                        />
                    )}
                    {extraFields}
                </div>
            )}

            <DataTable
                value={dataSource}
                emptyMessage={<NoRecordFound message={noRecordFoundMessage} />}
                // paginator={dataSource.length > 0 && paginator}
                paginator={rest?.loading ? false : paginator}
                rows={effectiveRows}
                rowsPerPageOptions={effectiveRowsPerPageOptions}
                sortMode={sortMode}
                tableStyle={tableStyle}
                scrollable={true}
                filters={filters}
                filterDisplay={filtersConfig?.filterDisplay || ""} // 'row' | 'menu'
                globalFilterFields={filtersConfig?.filterFields || globalFilterFields}
                scrollHeight={scrollHeight || "50vh"}
                dataKey={rest.dataKey || "id"}
                expandedRows={expandedRows}
                onRowToggle={handleInternalRowToggle}
                onRowClick={handleInternalRowClick}
                paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                {...rest}
                multiSortMeta={multiSortMeta}
                {...(showAllPaginatorTemplate && { paginatorTemplate: showAllPaginatorTemplate })}
                {...(isShowingAll && { virtualScrollerOptions: { itemSize: 48 } })}
                {...(onPage && { onPage: handleInternalPage })}
                {...onSort && { onSort: handleInternalSort }}>

                {resolvedColumns?.map((col, index) => {
                    if ((col.field === "actions" || col.type === GridColumnType.ACTION) && !col._resolvedActions) return null;

                    return (
                        <Column
                            key={col.field || index}
                            field={col.field}
                            header={col.header}
                            sortable={col?.config?.sortable}
                            body={getColumnBody(col)}
                            style={{ width: col.width }}
                            className={col.className}
                            filter={!!col?.config?.filter}
                            filterField={col.field}
                            filterElement={renderColumnFilter(col)}
                            selectionMode={col.selectionMode}
                            expander={col.expander}
                        />
                    );
                })}
            </DataTable>
        </div>
    );
};

GridBuilderComponent.propTypes = {
    dataSource: PropTypes.array,
    config: PropTypes.shape({
        columns: PropTypes.array,
        showAllOption: PropTypes.bool
    }),
    paginator: PropTypes.bool,
    rows: PropTypes.number,
    rowsPerPageOptions: PropTypes.array,
    sortMode: PropTypes.string,
    tableStyle: PropTypes.object,
    actionHandlers: PropTypes.object,
    columnEvents: PropTypes.object,
    enableSearch: PropTypes.bool,
    searchPlaceholder: PropTypes.string,
    scrollHeight: PropTypes.string,
    extraFields: PropTypes.node,
};

export const GridBuilder = React.memo(GridBuilderComponent);